import { memo } from "react"
import { ChevronDown, ChevronLeft, ChevronRight, FileCode, Plus, Search } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import Loader from "@/components/ui/loader"

import { QueryActionsDropdown } from "./query-actions-dropdown"
import type { SavedQueryItem } from "@/lib/http/editor"

export const SavedQueriesSidebar = memo(function SavedQueriesSidebar(props: {
  sidebarCollapsed: boolean
  onBack: () => void
  searchQuery: string
  setSearchQuery: (value: string) => void
  onNewQuery: () => void
  expandedSections: { favorites: boolean; private: boolean }
  toggleSection: (section: "favorites" | "private") => void
  favoriteQueries: SavedQueryItem[]
  privateQueries: SavedQueryItem[]
  savedListLoading: boolean
  activeSavedId?: string
  onOpenSavedQuery: (q: SavedQueryItem) => void
  onRename: (q: SavedQueryItem) => void
  onDuplicate: (q: SavedQueryItem) => void
  onToggleFavorite: (q: SavedQueryItem) => void
  onDelete: (q: SavedQueryItem) => void
}) {
  if (props.sidebarCollapsed) return null

  return (
    <div className="flex h-screen w-64 shrink-0 flex-col border-r border-gray-200 bg-white dark:border-gray-800 dark:bg-[#0F0F12]">
      <div className="shrink-0 border-b border-gray-200 px-3 py-2 dark:border-gray-800">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={props.onBack} className="h-7 px-2 text-xs">
            <ChevronLeft className="mr-1 h-3.5 w-3.5" />
            Back
          </Button>
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Query Editor</h2>
        </div>
      </div>

      <div className="shrink-0 border-b border-gray-200 p-2 dark:border-gray-800">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
          <Input
            placeholder="Search queries..."
            value={props.searchQuery}
            onChange={(e) => props.setSearchQuery(e.target.value)}
            className="h-8 pl-7 pr-7 text-xs"
          />
          <button className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <ChevronDown className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={props.onNewQuery}
            className="absolute right-7 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            aria-label="New query"
          >
            <Plus className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="border-b border-gray-200 dark:border-gray-800">
          <button
            onClick={() => props.toggleSection("favorites")}
            className="flex w-full items-center justify-between px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800"
          >
            <span>FAVORITES ({props.favoriteQueries.length})</span>
            {props.expandedSections.favorites ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            )}
          </button>
          {props.expandedSections.favorites && (
            <div className="px-3 pb-1.5">
              {props.savedListLoading ? (
                <div className="flex justify-center py-4">
                  <Loader className="h-4 w-4" />
                </div>
              ) : props.favoriteQueries.length === 0 ? (
                <div className="py-5 text-center text-xs text-gray-500 dark:text-gray-400">
                  <p className="text-xs">No favorite queries</p>
                  <p className="mt-1 text-[10px] leading-tight">Save a query to favorites for easy accessibility.</p>
                </div>
              ) : (
                props.favoriteQueries.map((q) => (
                  <div key={q.id} className="group relative">
                    <button
                      onClick={() => props.onOpenSavedQuery(q)}
                      className={`flex w-full items-center gap-1.5 rounded px-2 py-1 text-left text-xs ${
                        props.activeSavedId === q.id
                          ? "bg-gray-100 text-gray-900 dark:bg-gray-800 dark:text-white"
                          : "text-gray-600 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-gray-800"
                      }`}
                    >
                      <FileCode className="h-3.5 w-3.5 shrink-0" />
                      <span className="min-w-0 flex-1 truncate">{q.name}</span>
                    </button>
                    <QueryActionsDropdown
                      query={q}
                      onRename={props.onRename}
                      onDuplicate={props.onDuplicate}
                      onToggleFavorite={props.onToggleFavorite}
                      onDelete={props.onDelete}
                    />
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        <div className="border-b border-gray-200 dark:border-gray-800">
          <button
            onClick={() => props.toggleSection("private")}
            className="flex w-full items-center justify-between px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800"
          >
            <span>PRIVATE ({props.privateQueries.length})</span>
            {props.expandedSections.private ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            )}
          </button>
          {props.expandedSections.private && (
            <div className="px-3 pb-1.5">
              {props.savedListLoading ? (
                <div className="flex justify-center py-4">
                  <Loader className="h-4 w-4" />
                </div>
              ) : (
                props.privateQueries.map((q) => (
                  <div key={q.id} className="group relative">
                    <button
                      onClick={() => props.onOpenSavedQuery(q)}
                      className={`flex w-full items-center gap-1.5 rounded px-2 py-1 text-left text-xs ${
                        props.activeSavedId === q.id
                          ? "bg-gray-100 text-gray-900 dark:bg-gray-800 dark:text-white"
                          : "text-gray-600 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-gray-800"
                      }`}
                    >
                      <FileCode className="h-3.5 w-3.5 shrink-0" />
                      <span className="min-w-0 flex-1 truncate">{q.name}</span>
                    </button>
                    <QueryActionsDropdown
                      query={q}
                      onRename={props.onRename}
                      onDuplicate={props.onDuplicate}
                      onToggleFavorite={props.onToggleFavorite}
                      onDelete={props.onDelete}
                    />
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
})
